// src/pages/EmailRules.jsx
// Console → Email Settings → Email Rules (3.60.1, owner, 2026-10-14): fax by
// email and the Gmail clean-up (the card that was on Automations, renamed),
// and the clean-up log: every email thread the clean-up deleted from admin@,
// newest first, kept 12 months; who it was from and to, the subject and when,
// never the content. Backend: /portal/control/gmail (the switch) and
// /portal/email-log/cleanup (routes/emailLog.js, lib/gmailPurge.js).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import { GmailSwitches } from './Automations';
import EmptyState from '../components/EmptyState';
import Loader from '../components/Loader';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import Emo from '../components/Emo';
import SortTh, { useSort } from '../components/SortTh';

const DAYS = [1, 7, 30, 90, 365];
const RULES = {
  'Rule 1': 'Copies of received faxes',
  'Rule 2': 'Fax status emails to clients',
  'Rule 3': 'Faxes clients sent by email (read)',
  'Rule 4': 'Our test emails (read)',   // 3.60.4
  'Rule 5': 'Sent copies of our emails',   // 3.60.6
  'Rule 6': 'Sign-in emails from noreply@',   // 3.60.7
};
function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' ' + tzLabel(new Date(iso));
}
const ruleText = r => { const k = Object.keys(RULES).find(x => String(r || '').startsWith(x)); return k ? `${k}: ${RULES[k]}${/\(sent\)/.test(r) ? ' (Sent)' : ''}` : r || '—'; };

export default function EmailRules() {
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [f, setF] = useState({ days: 7, client: '', rule: '', q: '' });
  const [qInput, setQInput] = useState('');
  const [busy, setBusy] = useState(false);
  // 3.60.5 (owner): Show and a pager over the loaded entries
  const [show, setShow] = useState('50');
  // 3.62.18 (owner): sort by any column (the loaded entries)
  const { sorted, sort, onSort } = useSort(rows, { at: r => r.at, rule: r => r.rule, client: r => r.clientName || r.client, from: r => r.from, to: r => r.to, subject: r => r.subject, emailDate: r => r.emailDate });
  const { pageRows, pager } = usePager(sorted, show);

  const load = useCallback(async (before = null) => {
    setError('');
    try {
      const p = new URLSearchParams({ days: f.days, ...(f.client && { client: f.client }), ...(f.rule && { rule: f.rule }), ...(f.q && { q: f.q }), ...(before && { before }) });
      const r = await fetch(`${API_BASE}/portal/email-log/cleanup?${p}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed');
      setData(j);
      setRows(prev => (before ? [...prev, ...j.rows] : j.rows));
    } catch (e) { setError(e.message); }
  }, [getToken, f]);
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  async function download() {
    setBusy(true);
    try {
      const p = new URLSearchParams({ days: f.days, ...(f.client && { client: f.client }), ...(f.rule && { rule: f.rule }), ...(f.q && { q: f.q }) });
      const res = await fetch(`${API_BASE}/portal/email-log/cleanup/export?${p}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Download failed');
      const url = URL.createObjectURL(await res.blob());
      const name = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/)?.[1] || 'Gmail_clean-up.xlsx';
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { notify({ tone: 'error', title: "Couldn't download the clean-up log", detail: e.message }); }
    setBusy(false);
  }

  return (
    <div>
      <h3 style={S.title}><Emo e="📮" /> Email Rules</h3>
      <p style={S.sub}>
        How the fax mailboxes are handled. <strong>Fax by email</strong>: emails to fax@, privatefax@ and number addresses are read through the Gmail API and faxed by the rules.
        {' '}<strong>Gmail clean-up</strong>: every 6 hours, fax emails from the last 2 days are deleted from admin@ (the faxes stay in the portal; up to 300 a run, the rest at the next run; older ones were cleared once on Oct 15, 2026):
        {' '}Rule 1, copies of received faxes; Rule 2, fax status emails sent to clients and their copies to admin@; Rule 3, faxes clients sent by email, once read (unread ones stay, as they may need attention); Rule 4, our own test emails, once read;
        {' '}Rule 5, everything in Sent from fax@ and privatefax@ (copies: the people who got them keep theirs, and the Email Log has the record), except threads someone else wrote in; Rule 6, the sign-in emails sent from noreply@ (2-step verification added, password reset, email check).
        {' '}Our own alerts to admin@ (health, low balance, feedback, access requests, blocked access) are never deleted.
        {' '}<strong>Paused (counts only)</strong>: nothing is deleted, the runs only count; when it goes back to Live, the next runs also clear everything from the pause.
      </p>

      <GmailSwitches />

      <CleanupRuns />

      <h4 style={S.h4}>Clean-up log</h4>
      <p style={S.muted}>Every email thread the clean-up deleted, newest first, kept 12 months. Only who, to whom, the subject and when are kept, never the email itself.</p>
      <div style={S.filters}>
        <select style={S.select} value={f.days} onChange={e => setF({ ...f, days: Number(e.target.value) })} aria-label="Period">
          {DAYS.map(d => <option key={d} value={d}>{d === 1 ? 'Last 24 hours' : `Last ${d} days`}</option>)}
        </select>
        <select style={S.select} value={f.client} onChange={e => setF({ ...f, client: e.target.value })} aria-label="Client">
          <option value="">All clients</option>
          {(data?.clients || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={S.select} value={f.rule} onChange={e => setF({ ...f, rule: e.target.value })} aria-label="Rule">
          <option value="">All rules</option>
          {Object.entries(RULES).map(([k, v]) => <option key={k} value={k}>{k}: {v}</option>)}
        </select>
        <form onSubmit={e => { e.preventDefault(); setF({ ...f, q: qInput.trim() }); }} style={{ display: 'flex', gap: 6 }}>
          <input style={S.input} placeholder="From, to or subject" value={qInput} onChange={e => setQInput(e.target.value)} />
          <button type="submit" style={S.ghostBtn}>Search</button>
        </form>
        <ShowSelect value={show} onChange={setShow} style={S.select} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }} labelStyle={{ fontSize: 12, color: 'var(--t-546e7a)', fontWeight: 600 }} />
        <button style={S.ghostBtn} onClick={() => load()}><Emo e="↻" /> Refresh</button>
        <button style={S.ghostBtn} onClick={download} disabled={busy}>{busy ? 'Preparing…' : <><Emo e="📥" /> Download (Excel)</>}</button>
      </div>
      {error && <div style={S.error}>{error}</div>}
      {!data && !error ? <Loader /> : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr style={S.thead}>
              {[['at', 'Deleted'], ['rule', 'Rule'], ['client', 'Client'], ['from', 'From'], ['to', 'To'], ['subject', 'Subject'], ['emailDate', 'Email date']].map(([k, label]) =>
                <SortTh key={k} k={k} label={label} sort={sort} onSort={onSort} style={S.th} />)}
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 0' }}><EmptyState icon={<Emo e="🧹" />} emptyText="Nothing deleted in this period yet. The log starts with this version." /></td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={r.id} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmt(r.at)}{r.run === 'catch-up' && <div style={S.small}>catch-up</div>}</td>
                  <td style={{ ...S.td, fontSize: 12 }}>{ruleText(r.rule)}</td>
                  <td style={S.td}>{r.clientName || r.client}</td>
                  <td style={{ ...S.td, fontSize: 12, wordBreak: 'break-word', maxWidth: 220 }}>{r.from || '—'}</td>
                  <td style={{ ...S.td, fontSize: 12, wordBreak: 'break-word', maxWidth: 240 }}>{r.to || '—'}</td>
                  <td style={{ ...S.td, maxWidth: 320 }}>{r.subject || '—'}{r.messages > 1 && <div style={S.small}>{r.messages} emails in the thread</div>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmt(r.emailDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pager {...pager} label="deleted emails" />}
      {data?.next && <div style={{ textAlign: 'center', marginTop: 8 }}><button style={S.ghostBtn} onClick={() => load(data.next)}>Load older entries</button></div>}
    </div>
  );
}

// 3.62.18 (owner, Oct 16): every run, also those that deleted nothing, with
// the last run and the next one at the top (GET /portal/email-log/cleanup/runs)
function CleanupRuns() {
  const { getToken } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [show, setShow] = useState('50');
  const load = useCallback(async () => {
    setError('');
    try {
      const r = await fetch(`${API_BASE}/portal/email-log/cleanup/runs?days=${days}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed');
      setData(j);
    } catch (e) { setError(e.message); }
  }, [getToken, days]);
  useEffect(() => { load(); }, [load]);
  const runs = data?.runs || [];
  const { sorted, sort, onSort } = useSort(runs, { at: r => r.at, mode: r => r.mode, threads: r => r.threads ?? -1, deleted: r => r.deleted ?? -1, result: r => (r.error ? 'b' : 'a') });
  const { pageRows, pager } = usePager(sorted, show);
  const byRule = r => Object.entries(r.rules || {}).filter(([, v]) => v.deleted || v.threads).map(([k, v]) => `${k}: ${v.deleted || v.threads}`).join(' · ');
  return (
    <>
      <h4 style={S.h4}>Clean-up runs</h4>
      <p style={S.muted}>
        {data?.last ? <>Last run <strong>{fmt(data.last.at)}</strong>: {data.last.deleted ? `${data.last.deleted} deleted` : 'nothing old enough to delete'}{data.last.complete === false ? ' (the rest goes next run)' : ''}.</> : 'No run recorded yet.'}
        {data?.nextAt && <> Next run <strong>{fmt(data.nextAt)}</strong>.</>}
        {' '}Every run is listed, also those that found nothing to delete (since this version).
      </p>
      <div style={S.filters}>
        <select style={S.select} value={days} onChange={e => setDays(Number(e.target.value))} aria-label="Runs period">
          {DAYS.map(d => <option key={d} value={d}>{d === 1 ? 'Last 24 hours' : `Last ${d} days`}</option>)}
        </select>
        <ShowSelect value={show} onChange={setShow} style={S.select} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }} labelStyle={{ fontSize: 12, color: 'var(--t-546e7a)', fontWeight: 600 }} />
        <button style={S.ghostBtn} onClick={load}><Emo e="↻" /> Refresh</button>
      </div>
      {error && <div style={S.error}>{error}</div>}
      {!data && !error ? <Loader /> : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr style={S.thead}>
              {[['at', 'Ran'], ['mode', 'Mode'], ['threads', 'Found'], ['deleted', 'Deleted'], ['result', 'Result']].map(([k, label]) =>
                <SortTh key={k} k={k} label={label} sort={sort} onSort={onSort} style={S.th} />)}
              <th style={S.th}>By rule</th>
            </tr></thead>
            <tbody>
              {runs.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '28px 0', color: 'var(--t-78909c)', fontSize: 13 }}>No runs recorded in this period yet.</td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={r.id} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmt(r.at)}</td>
                  <td style={{ ...S.td, fontSize: 12 }}>{r.mode === 'live' ? 'Live' : r.mode === 'watch' ? 'Paused (counts only)' : r.mode}</td>
                  <td style={S.td}>{r.threads ?? '—'}</td>
                  <td style={S.td}>{r.mode === 'live' ? (r.deleted ?? '—') : '—'}</td>
                  <td style={{ ...S.td, fontSize: 12 }}>{r.error ? <span style={{ color: 'var(--t-b71c1c)' }}>Failed: {r.error}</span> : r.complete === false ? 'Stopped at its limit (the rest next run)' : r.threads ? 'Done' : 'Nothing to delete'}</td>
                  <td style={{ ...S.td, fontSize: 12 }}>{byRule(r) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pager {...pager} label="runs" />}
    </>
  );
}

const S = {
  title: { margin: '0 0 6px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub: { margin: '0 0 14px', color: 'var(--t-546e7a)', fontSize: 13, lineHeight: 1.6, maxWidth: 1000 },
  h4: { margin: '22px 0 4px', color: 'var(--t-00695c)', fontSize: 15 },
  muted: { color: 'var(--t-78909c)', fontSize: 13, margin: '0 0 10px' },
  small: { fontSize: 11, color: 'var(--t-78909c)', marginTop: 2 },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  select: { padding: '7px 10px', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  input: { padding: '7px 10px', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 200, background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  ghostBtn: { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)' },
  td: { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
};
